import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  ArrowRight,
  BarChart3,
  Check,
  CircleAlert,
  ExternalLink,
  Link2,
  Loader2,
  LogOut,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import type { InsightSnapshot, IntegrationStatus, SessionInfo } from '../shared/model';
import { Brand } from './identity';
import { EmailLoginRequest } from './EmailLogin';
import {
  formatMetaInstant,
  metaAuthorizationLabels,
  metaAuthorizationSourceLabels,
} from '../shared/meta';
import type { WorkQueueItem } from '../shared/work-queue';

async function post(path: string, csrf: string, body: unknown = {}) {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': csrf },
    body: JSON.stringify(body),
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Não foi possível concluir.');
  return result;
}
const connectionLabel = {
  not_configured: 'Configuração pendente',
  disconnected: 'Não conectada',
  connected: 'Conectada',
  expired: 'Reconectar',
  error: 'Revisar conexão',
};
export const metaLabel = (status: IntegrationStatus) =>
  status.authorization &&
  ['expiring', 'expired', 'access_lost'].includes(status.authorization.state)
    ? metaAuthorizationLabels[status.authorization.state]
    : connectionLabel[status.status];

/**
 * Entrar ou reconectar com Facebook grava uma conexão nova, ainda sem contas. Lê as contas uma
 * vez, sozinho, pela mesma rota de "Ler contas autorizadas" (a chamada à Meta fica fora de
 * transação no servidor). Uma falha fica registrada na conexão (`lastError`) e não se repete.
 */
export function useFirstAccountRead(
  integration: IntegrationStatus | undefined,
  csrf: string | undefined,
  refresh: () => Promise<unknown>,
) {
  const started = useRef(false);
  const [reading, setReading] = useState(false);
  const pending =
    integration?.status === 'connected' &&
    !!integration.identity &&
    !integration.lastSyncAt &&
    !integration.lastError &&
    !integration.accounts?.length;
  useEffect(() => {
    if (!pending || !csrf || started.current) return;
    started.current = true;
    setReading(true);
    post('/api/meta/accounts/sync', csrf)
      .catch(() => undefined)
      .then(() => refresh())
      .catch(() => undefined)
      .finally(() => setReading(false));
  }, [pending, csrf, refresh]);
  return reading;
}

export function Login({ session }: { session: SessionInfo }) {
  const [invite, setInvite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(
    new URLSearchParams(location.search).get('login') === 'failed'
      ? 'Não foi possível concluir o login. Confira o convite, o perfil usado e o consentimento; depois tente novamente.'
      : '',
  );
  const needsHttps = session.loginOrigin && location.origin !== session.loginOrigin;
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const result = await post('/api/meta/login', session.csrf, { invite });
      location.assign(result.redirectUrl);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }
  return (
    <main className="meta-login">
      <section className="panel login-card">
        <div className="brand">
          <Brand />
        </div>
        <span className="eyebrow">APLICATIVO.NET</span>
        <h1>Seu conteúdo. Suas conexões.</h1>
        <p>
          Acesso restrito à equipe convidada. Entre com o Facebook: as contas da Meta autorizadas a
          você são lidas logo em seguida.
        </p>
        {error && (
          <div className="meta-message error" role="alert">
            <CircleAlert size={18} />
            {error}
          </div>
        )}
        {needsHttps ? (
          <a className="button primary" href={session.loginOrigin}>
            Abrir piloto seguro <ArrowRight size={16} />
          </a>
        ) : (
          <form onSubmit={submit}>
            <button className="primary" disabled={busy || !session.integration.capabilities.login}>
              {busy ? <Loader2 size={17} className="spin" /> : <Link2 size={17} />}Entrar com
              Facebook
            </button>
            <details className="login-option">
              <summary>Primeiro acesso? Usar convite do FLG</summary>
              <label className="field">
                <span>
                  Convite do FLG <small>· apenas no primeiro acesso</small>
                </span>
                <input
                  autoComplete="off"
                  type="password"
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                  placeholder="Cole seu convite de uso único"
                  maxLength={100}
                />
              </label>
              <small>Cole o convite e clique em Entrar com Facebook.</small>
            </details>
          </form>
        )}
        {!session.integration.capabilities.login && !session.emailLogin?.available && (
          <div className="meta-message">
            <CircleAlert size={18} />
            <div>
              O piloto aguarda a configuração do app FLG na Meta.
              <small>O acesso aos dados fica fechado até o login estar disponível.</small>
            </div>
          </div>
        )}
        {!needsHttps && session.emailLogin?.enabled && (
          // Reserva para Meta indisponível ou autorização vencida; aberta quando o Facebook não está.
          <details className="login-option" open={!session.integration.capabilities.login}>
            <summary>Sem Facebook? Receber link por e-mail</summary>
            <EmailLoginRequest session={session} />
          </details>
        )}
        <div className="login-privacy">
          <ShieldCheck size={16} />
          <span>Somente convidados · sem publicação automática</span>
        </div>
        <a className="text-button" href="/auth/privacy" target="_blank" rel="noreferrer">
          Como usamos seus dados <ExternalLink size={12} />
        </a>
      </section>
    </main>
  );
}

export function MetaConnection({
  integration,
  csrf,
  refresh,
  connectionAlerts = [],
  readingAccounts = false,
}: {
  integration: IntegrationStatus;
  csrf: string;
  refresh: () => Promise<unknown>;
  connectionAlerts?: WorkQueueItem[];
  readingAccounts?: boolean;
}) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState(
    new URLSearchParams(location.search).get('login') === 'failed'
      ? 'A conexão com a Meta não foi concluída. Confira o perfil e o consentimento e tente novamente. Sua sessão editorial foi mantida.'
      : '',
  );
  const [confirm, setConfirm] = useState(false);
  async function run(name: string, path: string) {
    setBusy(name);
    setError('');
    try {
      const result = await post(path, csrf);
      if (result.redirectUrl) location.assign(result.redirectUrl);
      else {
        await refresh();
        setConfirm(false);
      }
    } catch (e) {
      setError((e as Error).message);
      await refresh();
    } finally {
      setBusy('');
    }
  }
  const accounts = integration.accounts || [];
  const authorization = integration.authorization;
  return (
    <section className="panel meta-connection">
      {connectionAlerts.length > 0 && (
        <section aria-label="Orientações das conexões da equipe">
          <h2>Conexões que precisam de atenção</h2>
          <p>A reconexão deve ser feita pelo responsável, usando a própria sessão no FLG.</p>
          {connectionAlerts
            .filter((item) => item.kind === 'connection')
            .map((item) => (
              <div className="meta-message" key={item.id}>
                <CircleAlert size={18} />
                <div>
                  <strong>{item.title}</strong>
                  <p>
                    Responsável: {item.owner || 'a definir'} · {item.context}
                  </p>
                  <ul>
                    {item.reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
        </section>
      )}
      <span className="connection-symbol meta">∞</span>
      <div className="section-heading">
        <h2>Meta · leitura de contas</h2>
        <span className={`badge ${integration.status === 'connected' ? 'publicado' : ''}`}>
          {metaLabel(integration)}
        </span>
      </div>
      <p>{integration.message}</p>
      {error && (
        <div className="meta-message error" role="alert">
          {error}
        </div>
      )}
      {integration.identity && (
        <div className="meta-identity">
          <ShieldCheck size={18} />
          <div>
            <strong>{integration.identity.name}</strong>
            <small>Perfil autenticado · sessão individual</small>
          </div>
        </div>
      )}
      {integration.lastError && <div className="meta-message error">{integration.lastError}</div>}
      {authorization && (
        <div
          className={`meta-message ${['expired', 'access_lost'].includes(authorization.state) ? 'error' : ''}`}
          role="status"
          aria-label="Validade da autorização Meta"
        >
          <CircleAlert size={18} />
          <div>
            <strong>{metaAuthorizationLabels[authorization.state]}</strong>
            <p>{authorization.reason}</p>
            {authorization.expiresAt && (
              <p>
                Primeiro vencimento:{' '}
                {authorization.deadlineSource === 'data_access' ? 'acesso aos dados' : 'token'} ·{' '}
                {formatMetaInstant(authorization.expiresAt)}
              </p>
            )}
            {authorization.action && <p>{authorization.action}</p>}
            <details>
              <summary>Fonte e prazos da autorização</summary>
              <p>
                Token:{' '}
                {authorization.tokenExpiresAt
                  ? formatMetaInstant(authorization.tokenExpiresAt)
                  : 'validade não informada pela Meta'}
                .
              </p>
              <p>
                Acesso aos dados:{' '}
                {authorization.dataAccessExpiresAt
                  ? formatMetaInstant(authorization.dataAccessExpiresAt)
                  : 'validade não informada pela Meta'}
                .
              </p>
              <p>
                Fonte: {metaAuthorizationSourceLabels[authorization.evidenceSource]}
                {authorization.observedAt
                  ? ` · ${formatMetaInstant(authorization.observedAt)}`
                  : ''}
                .
              </p>
              <p>
                Aviso antecipado: {authorization.warningDays} dias. Conferido em{' '}
                {formatMetaInstant(authorization.checkedAt)}.
              </p>
            </details>
          </div>
        </div>
      )}
      {integration.status === 'not_configured' ? (
        <>
          <div className="meta-message">
            <CircleAlert size={18} />
            <div>
              <strong>App exclusivo do FLG</strong>
              <small>
                Login, leitura de Páginas e Instagram profissional. Use um endereço HTTPS temporário
                para validar o piloto.
              </small>
            </div>
          </div>
          <details>
            <summary>Itens de configuração</summary>
            <ul className="config-list">
              {integration.missing?.map((key) => (
                <li key={key}>
                  <code>{key}</code>
                </li>
              ))}
            </ul>
            <p className="fine-print">
              As credenciais são configuradas no servidor. Não cole segredos no chat ou em campos do
              navegador.
            </p>
          </details>
          <button disabled>
            <Link2 size={16} />
            Entrar com Facebook · configurar app
          </button>
        </>
      ) : (
        <>
          <div className="meta-actions">
            <button
              className="primary"
              disabled={!!busy}
              onClick={() => run('login', '/api/meta/login')}
            >
              <Link2 size={16} />
              {integration.identity ? 'Reconectar Meta' : 'Conectar Meta'}
            </button>
            {integration.identity && (
              <button
                disabled={!!busy || readingAccounts || integration.status === 'expired'}
                onClick={() => run('sync', '/api/meta/accounts/sync')}
              >
                <RefreshCw size={16} className={busy === 'sync' || readingAccounts ? 'spin' : ''} />
                Ler contas autorizadas
              </button>
            )}
          </div>
          {integration.lastSyncAt && (
            <p className="fine-print">
              Contas conferidas em {new Date(integration.lastSyncAt).toLocaleString('pt-BR')} ·{' '}
              {integration.apiVersion}
            </p>
          )}
          {accounts.length ? (
            <div className="meta-accounts">
              {accounts.map((a) => (
                <article key={a.id}>
                  <span className={`account-network ${a.platform}`}>
                    {a.platform === 'instagram' ? 'IG' : 'f'}
                  </span>
                  <div>
                    <strong>{a.username ? `@${a.username}` : a.name}</strong>
                    <small>
                      {a.platform === 'instagram' ? 'Instagram profissional' : 'Página do Facebook'}{' '}
                      · {a.externalId}
                    </small>
                    <small className={a.canReadInsights ? 'available' : ''}>
                      {a.canReadInsights
                        ? 'Permissões para métricas presentes · validar na consulta'
                        : `Faltam: ${a.missingPermissions.join(', ')}`}
                    </small>
                  </div>
                  {a.canReadInsights && <Check size={17} />}
                </article>
              ))}
            </div>
          ) : (
            <div className="meta-message">
              <Link2 size={18} />
              <span>
                {integration.lastSyncAt
                  ? 'Nenhuma Página foi devolvida pela Meta. Confira os ativos concedidos e suas funções no app.'
                  : 'As contas são lidas automaticamente depois de conectar; se não aparecerem, clique em “Ler contas autorizadas”. Contas pessoais do Instagram não aparecem nesse fluxo.'}
              </span>
            </div>
          )}
          {!!integration.permissions?.length && (
            <details>
              <summary>Permissões concedidas</summary>
              <div className="scope-tags">
                {integration.permissions.map((p) => (
                  <code key={p}>{p}</code>
                ))}
              </div>
            </details>
          )}
          {!authorization && (
            <p className="fine-print">
              Validade:{' '}
              {integration.expiresAt
                ? formatMetaInstant(integration.expiresAt)
                : 'não informada pelo provedor'}
              . A autorização também pode ser revogada antes dessa data.
            </p>
          )}
          {integration.identity &&
            (confirm ? (
              <div className="disconnect-confirm">
                <p>
                  Remover desta conexão as credenciais, contas e leituras salvas no banco ativo?
                  Seus conteúdos editoriais permanecem.
                </p>
                <button disabled={!!busy} onClick={() => run('disconnect', '/api/meta/disconnect')}>
                  Confirmar desconexão
                </button>
                <button onClick={() => setConfirm(false)}>Cancelar</button>
              </div>
            ) : (
              <button className="text-button" onClick={() => setConfirm(true)}>
                <LogOut size={14} />
                Desconectar e remover leituras
              </button>
            ))}
        </>
      )}
    </section>
  );
}

const utcDay = (offset = 0) => new Date(Date.now() + offset * 86400000).toISOString().slice(0, 10);
export function Metrics({
  integration,
  csrf,
  refresh,
}: {
  integration: IntegrationStatus;
  csrf: string;
  refresh: () => Promise<unknown>;
}) {
  const [account, setAccount] = useState(integration.accounts?.[0]?.id || '');
  const [since, setSince] = useState(utcDay(-7));
  const [until, setUntil] = useState(utcDay());
  const [snapshots, setSnapshots] = useState<InsightSnapshot[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (integration.identity)
      fetch('/api/meta/insights')
        .then(async (r) => {
          if (!r.ok) throw new Error('Não foi possível carregar as leituras.');
          return r.json();
        })
        .then(setSnapshots)
        .catch((e) => setError(e.message));
  }, [integration.identity?.id]);
  const selected = integration.accounts?.find((a) => a.id === account);
  const snapshot = snapshots
    .filter((s) => s.accountId === account && s.since === since && s.until === until)
    .at(-1);
  async function collect(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const next = await post('/api/meta/insights', csrf, { accountId: account, since, until });
      setSnapshots((s) => [...s, next]);
    } catch (e) {
      setError((e as Error).message);
      await refresh();
    } finally {
      setBusy(false);
    }
  }
  if (!integration.accounts?.length)
    return (
      <section className="panel metrics-empty">
        <BarChart3 size={40} />
        <h2>As métricas começam pela conexão</h2>
        <p>
          Conecte a Meta e leia as contas autorizadas. Os indicadores aparecerão após a primeira
          consulta.
        </p>
        <a className="button primary" href="#connections">
          Ir para Conexões <ArrowRight size={16} />
        </a>
      </section>
    );
  return (
    <>
      <section className="panel metrics-controls">
        <form onSubmit={collect}>
          <label className="field">
            <span>Conta</span>
            <select value={account} onChange={(e) => setAccount(e.target.value)}>
              {integration.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.platform === 'instagram' ? 'Instagram' : 'Facebook'} ·{' '}
                  {a.username ? `@${a.username}` : a.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>De (UTC)</span>
            <input
              type="date"
              value={since}
              max={utcDay(-1)}
              min={utcDay(-89)}
              onChange={(e) => setSince(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>Até (exclusivo, UTC)</span>
            <input
              type="date"
              value={until}
              max={utcDay()}
              onChange={(e) => setUntil(e.target.value)}
              required
            />
          </label>
          <button
            className="primary"
            disabled={busy || !selected?.canReadInsights || integration.status === 'expired'}
          >
            <RefreshCw size={16} className={busy ? 'spin' : ''} />
            {busy ? 'Consultando…' : 'Atualizar métricas'}
          </button>
        </form>
        <p className="fine-print">
          Janela de 1 a 30 dias completos, dentro dos últimos 89 dias. O dia final não entra na
          consulta. Valores pertencem apenas à conta selecionada.
        </p>
        {!selected?.canReadInsights && (
          <div className="meta-message error">
            Faltam permissões de leitura para esta conta. Revise a conexão.
          </div>
        )}
      </section>
      {error && (
        <div className="meta-message error" role="alert">
          {error} A última leitura bem-sucedida permanece visível.
        </div>
      )}
      {snapshot ? (
        <>
          <div className="metric-readout">
            {snapshot.metrics.map((m) => (
              <article key={m.key} className="panel metric-card">
                <span>{m.label}</span>
                <strong>{m.value === null ? '—' : m.value.toLocaleString('pt-BR')}</strong>
                <small>
                  {m.status === 'available'
                    ? m.aggregation === 'total'
                      ? 'Total devolvido pela Meta'
                      : 'Soma dos valores diários'
                    : m.status === 'unavailable'
                      ? 'Indisponível'
                      : 'Falha nesta métrica'}
                </small>
                <p>
                  {m.error ||
                    (m.status === 'unavailable'
                      ? 'A API não retornou um valor. Isso não equivale a zero.'
                      : '')}
                </p>
              </article>
            ))}
          </div>
          <div className="source-footer">
            <span className="dot" />
            <span>
              Leitura de {new Date(snapshot.collectedAt).toLocaleString('pt-BR')} ·{' '}
              {snapshot.apiVersion} · janela UTC
            </span>
          </div>
          <section className="panel metric-details">
            <h2>Origem e interpretação</h2>
            <p>
              Os valores podem mudar com o processamento da Meta. Alcance não é somado entre dias ou
              contas; totais de plataformas distintas permanecem separados.
            </p>
            {snapshot.metrics.map((m) => (
              <details key={m.key}>
                <summary>
                  {m.label} · {m.key}
                </summary>
                <p>{m.description || 'Sem descrição adicional devolvida pela API.'}</p>
                {m.points.length > 0 && (
                  <table>
                    <thead>
                      <tr>
                        <th>Fim do intervalo informado pela Meta</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {m.points.map((p, i) => (
                        <tr key={i}>
                          <td>{p.endTime}</td>
                          <td>{p.value.toLocaleString('pt-BR')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </details>
            ))}
          </section>
        </>
      ) : (
        <section className="panel metrics-empty">
          <BarChart3 size={34} />
          <h2>Nenhuma leitura para esta janela</h2>
          <p>Clique em Atualizar métricas para consultar a Meta. Não há números simulados.</p>
        </section>
      )}
    </>
  );
}
